import axios from "axios";
import BASE_URL from "../_helpers/constants";
import { readCurrentUser } from "../_helpers/currentUser";

// Pooled inbox SP3 — the client's onboarding, documents and consents calls
// (contract: Astra-Backend docs/superpowers/plans/2026-09-26-pooled-inbox-sp3-plan.md
// § The shared contract). The inbox.services.js style: each returns the axios
// response and throws on a non-2xx; callers read `res.data` and
// `err.response.data`.
const authHeaders = () => ({
  Authorization: `Bearer ${readCurrentUser()?.token ?? ""}`,
});

// ---- Stage and welcome (W3) ------------------------------------------------------
// Public: the welcome screen is shown before sign-in, so no Authorization.
export const getOnboardingConfig = () =>
  axios.get(`${BASE_URL}/onboarding/config`);

export const getStage = () =>
  axios.get(`${BASE_URL}/onboarding/stage`, { headers: authHeaders() });

// ---- The acknowledgements page (W4) ----------------------------------------------
export const getDisclosures = () =>
  axios.get(`${BASE_URL}/onboarding/disclosures`, { headers: authHeaders() });

export const acknowledge = ({ versionIds, expandedMs }) =>
  axios.post(
    `${BASE_URL}/onboarding/acknowledge`,
    { versionIds, expandedMs },
    { headers: authHeaders() },
  );

// ---- The agreement page (W5) ------------------------------------------------------
export const getAgreement = () =>
  axios.get(`${BASE_URL}/onboarding/agreement`, { headers: authHeaders() });

// The pre-sign copy: PDF bytes through the same renderer, no signature block.
export const downloadAgreementPdf = () =>
  axios.get(`${BASE_URL}/onboarding/agreement/pdf`, {
    headers: authHeaders(),
    responseType: "blob",
  });

// The server accepts exactly these five fields (any other key is 400
// bad_body); every other evidence field is derived server-side.
export const signAgreement = ({
  documentVersionId,
  typedName,
  esignTicked,
  endMarkerReachedAt,
  gateMode,
}) =>
  axios.post(
    `${BASE_URL}/onboarding/agreement/sign`,
    { documentVersionId, typedName, esignTicked, endMarkerReachedAt, gateMode },
    { headers: authHeaders() },
  );

// ---- The consents page and the receipt (W6) --------------------------------------
export const getConsents = () =>
  axios.get(`${BASE_URL}/onboarding/consents`, { headers: authHeaders() });

// `choices` is `{ [kind]: boolean }` with exactly the served rows' kinds.
export const submitConsents = (choices) =>
  axios.post(`${BASE_URL}/onboarding/consents`, choices, {
    headers: authHeaders(),
  });

export const getReceipt = () =>
  axios.get(`${BASE_URL}/onboarding/receipt`, { headers: authHeaders() });

export const continueOnboarding = () =>
  axios.post(`${BASE_URL}/onboarding/continue`, {}, { headers: authHeaders() });

// ---- You › Documents ---------------------------------------------------------
export const getMyDocuments = () =>
  axios.get(`${BASE_URL}/documents/me`, { headers: authHeaders() });

// R135: the server answers application/pdf bytes; a bare link cannot carry the
// Bearer token, so the page fetches the bytes and opens them with openBlob.
export const downloadDocument = (id) =>
  axios.get(`${BASE_URL}/documents/${id}/download`, {
    headers: authHeaders(),
    responseType: "blob",
  });

// ---- Settings › Consents -------------------------------------------------------
export const getSettingsConsents = () =>
  axios.get(`${BASE_URL}/consents`, { headers: authHeaders() });

export const setConsent = (kind, state) =>
  axios.post(
    `${BASE_URL}/consents`,
    { kind, state },
    { headers: authHeaders() },
  );

// Hands a fetched PDF to the browser as a download: an object URL on a
// throwaway anchor, revoked once the click has been dispatched. 1500ms, not
// 0: Safari and older Firefox can still be resolving the download when the
// URL is revoked on the same tick, cancelling it.
export function openBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// A blob request's error body arrives as a Blob too; this reads the server's
// `message` out of it (or out of a plain JSON body), or null.
export async function responseMessage(err) {
  const data = err?.response?.data;
  try {
    if (typeof Blob !== "undefined" && data instanceof Blob) {
      return JSON.parse(await data.text())?.message ?? null;
    }
    return data?.message ?? null;
  } catch (_) {
    return null;
  }
}
